import { RefObject } from "react";
import { Besley, BesleyBold } from "./base64Font";
import { PDFDocument, rgb } from "pdf-lib";
import { saveAs } from "file-saver";
import fontkit from "@pdf-lib/fontkit";
import {
  transitionTableGenerator,
  overviewTableGenerator,
} from "./tableGenerator";

const convertSvgToPng = (svgElement: SVGElement): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const svgString = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.onload = () => {
      const scale = 3;
      const canvas = document.createElement("canvas");
      canvas.width = image.width * scale;
      canvas.height = image.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Canvas context is null");

      // Scale the context to get higher resolution rendering
      ctx.scale(scale, scale);
      ctx.drawImage(image, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) return reject("Failed to convert canvas to blob");
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as ArrayBuffer);
        };
        reader.readAsArrayBuffer(blob);
      }, "image/png");
    };
    image.onerror = reject;
    image.src = url;
  });
};

function formatDate(dateString: Date | string) {
  let date: Date;
  if (typeof dateString === "string") {
    const [year, month, day] = dateString.split("-").map(Number);
    date = new Date(year, month - 1, day); // month is 0-based
  } else {
    date = dateString;
  }

  const day = date.getDate();
  const year = date.getFullYear();

  const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(
    date
  );

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${month} ${day}${getOrdinalSuffix(day)} ${year}`;
}

export const generatePDF = async (
  chartRef: RefObject<HTMLElement | null>,
  detailData: Record<
    string,
    { month: string; paymentAmount: number; bonus: number }
  >,
  totalClients: number,
  totalAccountValue: number,
  numberOfWeeks: number,
  weeksData: { numberOfClients: number; accountValue: number; week: number }[],
  plotData: { month: string; Payment: number; Bonus: number }[],
  inputName: string | null,
  startDate: Date,
  lastDate: string
) => {
  const svgElement = chartRef.current?.querySelector("svg");
  if (!svgElement) return;

  try {
    const pngImageBytes = await convertSvgToPng(svgElement);

    const existingPdfBytes = await fetch("/template.pdf").then((res) =>
      res.arrayBuffer()
    );

    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Register fonts
    pdfDoc.registerFontkit(fontkit);
    const besleyBoldFontBytes = Uint8Array.from(atob(BesleyBold), (c) =>
      c.charCodeAt(0)
    );
    const beskeyFontBytes = Uint8Array.from(atob(Besley), (c) =>
      c.charCodeAt(0)
    );
    const besleyBold = await pdfDoc.embedFont(besleyBoldFontBytes, {
      subset: true,
    });
    const besley = await pdfDoc.embedFont(beskeyFontBytes, { subset: true });
    const fontColor = rgb(47 / 255, 59 / 255, 76 / 255);

    const leftPadding = 60;

    // Cover Page
    const coverPage = pdfDoc.getPages()[0];
    const todayDate = new Date().toLocaleDateString("en-US");

    coverPage.drawText(todayDate, {
      x: 100,
      y: 313,
      size: 15,
      font: besleyBold,
      color: fontColor,
    });

    coverPage.drawText(inputName ?? "", {
      x: 110,
      y: 378,
      size: 15,
      font: besleyBold,
      color: fontColor,
    });

    // Transition Schedule Page (Page 3)
    const transitionSchedulePage = pdfDoc.getPages()[1];
    const { width, height } = transitionSchedulePage.getSize();

    // Overview Data
    const firstPaymentEntry = plotData.find((entry) => entry.Payment > 0);
    const firstPaymentDate = firstPaymentEntry
      ? `${firstPaymentEntry.month.split(" ")[0]} 15th ${
          firstPaymentEntry.month.split(" ")[1]
        }`
      : "N/A";

    const firstBonusEntry = plotData.find((entry) => entry.Bonus > 0);
    const firstBonusDate = firstBonusEntry
      ? `${firstBonusEntry.month.split(" ")[0]} 15th ${
          firstBonusEntry.month.split(" ")[1]
        }`
      : "N/A";

    const overviewData = [
      {
        label: "Transition Start Date",
        value: formatDate(startDate),
      },
      {
        label: "Number of Planned Transition Weeks",
        value: numberOfWeeks.toString(),
      },
      {
        label: "Total Number of Households",
        value: totalClients.toLocaleString(),
      },

      {
        label: "Total Book Value ($)",
        value: `$${totalAccountValue.toLocaleString()}`,
      },
      {
        label: "Expected Transition Completion Date",
        value: formatDate(lastDate),
      },
      {
        label: "Date of First Ongoing Monthly Payment Received",
        value: firstPaymentDate,
      },
      {
        label: "Date of First Transition Bonus Payment Received",
        value: firstBonusDate,
      },
    ];

    const overviewColumnWidths = [335, 175];
    const overviewTableWidth = overviewColumnWidths.reduce((a, b) => a + b, 0);
    const overviewTableX = (width - overviewTableWidth) / 2;
    const overviewTableY = height - 300;
    const rowHeight = 20;

    // Generate overview table
    overviewTableGenerator(
      transitionSchedulePage,
      overviewTableX,
      overviewTableY,
      overviewColumnWidths,
      rowHeight,
      overviewData,
      besley,
      besleyBold,
      fontColor
    );

    const weeklyTransitionSummaryPage = pdfDoc.getPages()[1];

    // 2. Transition Schedule Table
    const transitionColumnWidths = [110, 200, 200];
    const transitionTableWidth = transitionColumnWidths.reduce(
      (a, b) => a + b,
      0
    );
    const transitionTableX = (width - transitionTableWidth) / 2 - 60;
    const transitionTableY = overviewTableY - 275;

    // Table Headers
    const headers = ["Week", "Number of Households", "Account Value ($)"];

    // Format table data
    const transitionTableData = weeksData.map(
      ({ numberOfClients, accountValue, week }) => [
        week.toString(),
        numberOfClients.toLocaleString(),
        `$${accountValue.toLocaleString()}`,
      ]
    );

    // Generate transition schedule table
    transitionTableGenerator(
      weeklyTransitionSummaryPage,
      transitionTableX,
      transitionTableY,
      transitionColumnWidths,
      rowHeight,
      headers,
      transitionTableData,
      besley,
      besleyBold,
      fontColor,
      leftPadding
    );

    // Projected Payments Page (Page 4)
    const projectedPaymentsPageNumber = 2;
    const projectedPaymentsPage =
      pdfDoc.getPages()[projectedPaymentsPageNumber];

    const columnWidths = [80, 130, 160, 140];
    const tableX = (width - columnWidths.reduce((a, b) => a + b, 0)) / 2 - 60;
    const tableY = height - 310;

    const projectionHeaders = [
      "Payroll\nPeriod",
      "Ongoing Monthly\nPayment ($)",
      "Transition Bonus\nPayment ($)",
      "Total Monthly\nPayment ($)",
    ];

    const projectionTableData = Object.values(detailData).map(
      ({ month, paymentAmount, bonus }) => {
        const totalPayment = paymentAmount + bonus;
        const [monthName, year] = month.split(" ");
        month = `${monthName} 15th ${year}`;
        return [
          month,
          paymentAmount === 0 ? "-" : `$${paymentAmount.toLocaleString()}`,
          bonus === 0 ? "-" : `$${bonus.toLocaleString()}`,
          totalPayment === 0 ? "-" : `$${totalPayment.toLocaleString()}`,
        ];
      }
    );

    transitionTableGenerator(
      projectedPaymentsPage,
      tableX,
      tableY,
      columnWidths,
      rowHeight,
      projectionHeaders,
      projectionTableData,
      besley,
      besleyBold,
      fontColor,
      leftPadding
    );

    // Projected Payments Table page

    const projectedPaymentsPageNumber2 = 2;
    const projectedPaymentsPage2 =
      pdfDoc.getPages()[projectedPaymentsPageNumber2];

    // Embed the chart image
    const image = await pdfDoc.embedPng(pngImageBytes);
    const originalWidth = image.width;
    const originalHeight = image.height;
    const maxWidth = 500;
    const scaleFactor = maxWidth / originalWidth;
    const scaledWidth = originalWidth * scaleFactor;
    const scaledHeight = originalHeight * scaleFactor;

    const imageX = (width - scaledWidth + leftPadding) / 2 - 30;
    const imageY = height - 750;

    projectedPaymentsPage2.drawImage(image, {
      x: imageX,
      y: imageY,
      width: scaledWidth,
      height: scaledHeight,
    });

    // === Custom Legend under the chart (centered & tighter) ===
    const squareSize = 8;
    const spacing = 6; // space between square and label
    const itemSpacing = 50; // reduced spacing between legend items
    const legendFontSize = 8;

    const label1 = "Ongoing Monthly Payment";
    const label2 = "Transition Bonus Payment";

    const darkBlue = rgb(47 / 255, 59 / 255, 76 / 255);
    const yellow = rgb(94 / 255, 128 / 255, 129 / 255);

    // Measure text widths
    const label1Width = besleyBold.widthOfTextAtSize(label1, legendFontSize);
    const label2Width = besleyBold.widthOfTextAtSize(label2, legendFontSize);

    // Total width = both squares + spacing + both labels + spacing between items
    const totalLegendWidth =
      squareSize +
      spacing +
      label1Width +
      itemSpacing +
      squareSize +
      spacing +
      label2Width;

    // Center the legend under the chart
    const legendX = imageX + (scaledWidth - totalLegendWidth) / 2;
    const legendY = imageY + 8; // adjust vertical position

    // Draw first item (Optimized Portfolio)
    projectedPaymentsPage2.drawRectangle({
      x: legendX,
      y: legendY,
      width: squareSize,
      height: squareSize,
      color: darkBlue,
    });

    projectedPaymentsPage2.drawText(label1, {
      x: legendX + squareSize + spacing,
      y: legendY - 1,
      size: legendFontSize,
      font: besleyBold,
      color: darkBlue,
    });

    // Draw second item (Current Portfolio)
    const secondItemX =
      legendX + squareSize + spacing + label1Width + itemSpacing;

    projectedPaymentsPage2.drawRectangle({
      x: secondItemX,
      y: legendY,
      width: squareSize,
      height: squareSize,
      color: yellow,
    });

    projectedPaymentsPage2.drawText(label2, {
      x: secondItemX + squareSize + spacing,
      y: legendY - 1,
      size: legendFontSize,
      font: besleyBold,
      color: yellow,
    });

    // Save PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    saveAs(blob, "Advisor Payment Projection Report.pdf");
  } catch (error) {
    console.error("Failed to generate PDF:", error);
  }
};
